/**
 * JavaScript / pixel tracking (ITP-compliant) — dash data-layer client
 * (feature #162).
 *
 * Thin typed wrapper around `apiClient` for the `/api/v1/js-pixel` surface.
 * Mirrors the contract in `apps/api/src/routes/js-pixel.ts`:
 *
 *   POST   /snippet                 body { siteLabel, allowedOrigin,
 *                                          eventTypes? } → { snippet }
 *   GET    /snippet                 → { snippets[] }
 *   GET    /snippet/:id             → { snippet }
 *   GET    /snippet/:id/loader.js   → text/javascript (public)
 *   PUT    /snippet/:id             body { siteLabel?, allowedOrigin?,
 *                                          eventTypes?, enabled? } → { snippet }
 *   DELETE /snippet/:id             → { ok, id }
 *   GET    /events/:id?limit=N      → { snippet_id, count, events[] }
 *
 * This module stays dependency-free beyond `apiClient`: the page component
 * and the unit tests both import from here, and the validators (`isValidOrigin`,
 * `draftSchema`, `sortSnippets`) are pure so they run in the bun:test
 * harness without React.
 *
 * Brand: emerald → sky, zinc borders, 6-8px rounded, light only. No emojis,
 * no competitor names.
 */

import { apiClient, ApiError, apiBaseUrl } from "@/api-client";

// ---------------------------------------------------------------------------
// Event types — must match the PIXEL_EVENT_TYPES tuple on the API side.
// ---------------------------------------------------------------------------

export const PIXEL_EVENT_TYPES = ["page", "click", "conv", "custom"] as const;
export type PixelEventType = (typeof PIXEL_EVENT_TYPES)[number];

export const PIXEL_EVENT_TYPE_LABELS: Record<PixelEventType, string> = {
	page: "Page view",
	click: "Click",
	conv: "Conversion",
	custom: "Custom",
};

/** Default selection for new snippets — matches the API default. */
export const DEFAULT_EVENT_TYPES: PixelEventType[] = ["page", "click", "conv"];

/** ITP ceiling — mirrored for UI display / validation of cookie_max_age. */
export const ITP_MAX_AGE_SECONDS = 7 * 24 * 60 * 60;

// ---------------------------------------------------------------------------
// Server view shapes — mirror rowToSnippetView / rowToEventView exactly.
// ---------------------------------------------------------------------------

export interface JsPixelSnippetView {
	id: string;
	site_label: string;
	allowed_origin: string;
	event_types: PixelEventType[];
	cookie_name: string;
	cookie_max_age: number;
	enabled: boolean;
	created_at: number;
	updated_at: number;
}

export interface JsPixelEventView {
	id: string;
	snippet_id: string;
	event_type: PixelEventType;
	visitor_id: string;
	url: string | null;
	referrer: string | null;
	itp_fallback: boolean;
	created_at: number;
}

export interface ListSnippetsResponse {
	snippets: JsPixelSnippetView[];
}

export interface GetSnippetResponse {
	snippet: JsPixelSnippetView;
}

export interface CreateSnippetResponse {
	snippet: JsPixelSnippetView;
}

export interface UpdateSnippetResponse {
	snippet: JsPixelSnippetView;
}

export interface DeleteSnippetResponse {
	ok: boolean;
	id: string;
}

export interface ListEventsResponse {
	snippet_id: string;
	count: number;
	events: JsPixelEventView[];
}

// ---------------------------------------------------------------------------
// Validation rules (mirror the API limits exactly)
// ---------------------------------------------------------------------------

export const SITE_LABEL_MAX = 128;
export const ALLOWED_ORIGIN_MAX = 512;

/** Canonicalise an origin string the same way the API does: http(s)://host[:port]
 *  with no path/query/fragment/userinfo. Returns null when invalid. */
export function parseAllowedOrigin(raw: unknown): string | null {
	if (typeof raw !== "string") return null;
	const trimmed = raw.trim();
	if (trimmed.length === 0 || trimmed.length > ALLOWED_ORIGIN_MAX) return null;
	let u: URL;
	try {
		u = new URL(trimmed);
	} catch {
		return null;
	}
	if (u.protocol !== "http:" && u.protocol !== "https:") return null;
	if (u.username !== "" || u.password !== "") return null;
	if (u.pathname !== "/" && u.pathname !== "") return null;
	if (u.search !== "") return null;
	if (u.hash !== "") return null;
	const port = u.port === "" ? "" : `:${u.port}`;
	return `${u.protocol}//${u.hostname}${port}`;
}

export function isValidOrigin(raw: unknown): boolean {
	return parseAllowedOrigin(raw) !== null;
}

// ---------------------------------------------------------------------------
// Snippet draft schema (hand-rolled safeParse surface — avoids adding zod)
// ---------------------------------------------------------------------------

export interface SnippetDraft {
	siteLabel: string;
	allowedOrigin: string;
	eventTypes: PixelEventType[];
}

export const EMPTY_DRAFT: SnippetDraft = {
	siteLabel: "",
	allowedOrigin: "",
	eventTypes: [...DEFAULT_EVENT_TYPES],
};

export interface ValidatedSnippetDraft {
	siteLabel: string;
	allowedOrigin: string;
	eventTypes: PixelEventType[];
}

export interface ParseOk<T> {
	success: true;
	data: T;
}
export interface ParseErr {
	success: false;
	errors: Record<string, string>;
}
export type ParseResult<T> = ParseOk<T> | ParseErr;

export const snippetDraftSchema = {
	safeParse(input: SnippetDraft): ParseResult<ValidatedSnippetDraft> {
		const errors: Record<string, string> = {};

		const siteLabel = (input.siteLabel ?? "").trim();
		if (siteLabel.length === 0) {
			errors.siteLabel = "Site label is required.";
		} else if (siteLabel.length > SITE_LABEL_MAX) {
			errors.siteLabel = `Site label must be ${SITE_LABEL_MAX} characters or fewer.`;
		}

		const origin = parseAllowedOrigin(input.allowedOrigin);
		if (!origin) {
			errors.allowedOrigin =
				"Enter a full origin such as https://brand.com — no path or query.";
		}

		const types = Array.isArray(input.eventTypes) ? input.eventTypes : [];
		const seen = new Set<PixelEventType>();
		for (const t of types) {
			if ((PIXEL_EVENT_TYPES as readonly string[]).includes(t)) {
				seen.add(t as PixelEventType);
			}
		}
		const normalised = sortEventTypes(Array.from(seen));
		if (normalised.length === 0) {
			errors.eventTypes = "Select at least one event type to track.";
		}

		if (Object.keys(errors).length > 0) return { success: false, errors };
		return {
			success: true,
			data: {
				siteLabel,
				allowedOrigin: origin as string,
				eventTypes: normalised,
			},
		};
	},
};

/** Canonical ordering so the UI pills render in a stable sequence. */
export function sortEventTypes(
	types: readonly PixelEventType[],
): PixelEventType[] {
	const order: Record<PixelEventType, number> = {
		page: 0,
		click: 1,
		conv: 2,
		custom: 3,
	};
	return [...types].sort((a, b) => order[a] - order[b]);
}

// ---------------------------------------------------------------------------
// Update-patch builder — only sends diffs so the PUT stays minimal.
// ---------------------------------------------------------------------------

export interface UpdatePatch {
	siteLabel?: string;
	allowedOrigin?: string;
	eventTypes?: PixelEventType[];
	enabled?: boolean;
}

export function diffDraft(
	original: JsPixelSnippetView,
	draft: SnippetDraft,
): UpdatePatch {
	const patch: UpdatePatch = {};
	const siteLabel = (draft.siteLabel ?? "").trim();
	if (siteLabel.length > 0 && siteLabel !== original.site_label) {
		patch.siteLabel = siteLabel;
	}
	const origin = parseAllowedOrigin(draft.allowedOrigin);
	if (origin && origin !== original.allowed_origin) {
		patch.allowedOrigin = origin;
	}
	const types = sortEventTypes(draft.eventTypes ?? []);
	const originalTypes = sortEventTypes(original.event_types);
	if (types.length > 0 && types.join(",") !== originalTypes.join(",")) {
		patch.eventTypes = types;
	}
	return patch;
}

// ---------------------------------------------------------------------------
// Formatting + presentation helpers
// ---------------------------------------------------------------------------

export function formatTimestamp(seconds: number | null | undefined): string {
	if (seconds === null || seconds === undefined || Number.isNaN(seconds))
		return "—";
	const d = new Date(seconds * 1000);
	if (Number.isNaN(d.getTime())) return "—";
	return d.toISOString().replace("T", " ").replace(/\.\d{3}Z$/, "Z");
}

/** Short relative-age string used in the event list (e.g. "3 min ago"). */
export function formatRelative(seconds: number, nowSeconds: number): string {
	if (!Number.isFinite(seconds) || !Number.isFinite(nowSeconds)) return "—";
	const delta = Math.max(0, Math.floor(nowSeconds - seconds));
	if (delta < 5) return "just now";
	if (delta < 60) return `${delta}s ago`;
	if (delta < 3600) return `${Math.floor(delta / 60)}m ago`;
	if (delta < 86_400) return `${Math.floor(delta / 3600)}h ago`;
	return `${Math.floor(delta / 86_400)}d ago`;
}

export function statusBadgeClass(enabled: boolean): string {
	return enabled
		? "bg-emerald-50 text-emerald-700 border-emerald-200"
		: "bg-zinc-100 text-zinc-600 border-zinc-200";
}

export function eventTypeBadgeClass(type: PixelEventType): string {
	switch (type) {
		case "conv":
			return "bg-emerald-50 text-emerald-700 border-emerald-200";
		case "click":
			return "bg-sky-50 text-sky-700 border-sky-200";
		case "custom":
			return "bg-amber-50 text-amber-700 border-amber-200";
		default:
			return "bg-zinc-50 text-zinc-700 border-zinc-200";
	}
}

// ---------------------------------------------------------------------------
// Sort + aggregation
// ---------------------------------------------------------------------------

/** Enabled first (most operator-relevant), then newest-created. */
export function sortSnippets(
	rows: readonly JsPixelSnippetView[],
): JsPixelSnippetView[] {
	return rows.slice().sort((a, b) => {
		if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
		return b.created_at - a.created_at;
	});
}

export function countByEnabled(
	rows: readonly JsPixelSnippetView[],
): { enabled: number; disabled: number } {
	let enabled = 0;
	let disabled = 0;
	for (const r of rows) {
		if (r.enabled) enabled += 1;
		else disabled += 1;
	}
	return { enabled, disabled };
}

export function tallyEventTypes(
	events: readonly JsPixelEventView[],
): Record<PixelEventType, number> {
	const out: Record<PixelEventType, number> = {
		page: 0,
		click: 0,
		conv: 0,
		custom: 0,
	};
	for (const e of events) {
		if ((PIXEL_EVENT_TYPES as readonly string[]).includes(e.event_type)) {
			out[e.event_type] += 1;
		}
	}
	return out;
}

export function itpFallbackRate(
	events: readonly JsPixelEventView[],
): number {
	if (events.length === 0) return 0;
	let hits = 0;
	for (const e of events) if (e.itp_fallback) hits += 1;
	return hits / events.length;
}

// ---------------------------------------------------------------------------
// Install snippet — the inline <script> tag an operator pastes into their
// site's <head>. Single src, no config needed. Loader is cached for 5 min
// server-side so repeat views are cheap.
// ---------------------------------------------------------------------------

export function buildInstallSnippet(snippet: JsPixelSnippetView): string {
	const src = `${apiBaseUrl}/js-pixel/snippet/${encodeURIComponent(snippet.id)}/loader.js`;
	return `<!-- Trackerity pixel for ${snippet.site_label} -->
<script async src="${src}"></script>`;
}

/** Clipboard helper — used by the "Copy snippet" button. Returns `true` on
 *  success so the caller can show a toast. Tolerant of non-browser envs. */
export async function copyToClipboard(text: string): Promise<boolean> {
	if (typeof navigator === "undefined") return false;
	try {
		if (navigator.clipboard && navigator.clipboard.writeText) {
			await navigator.clipboard.writeText(text);
			return true;
		}
	} catch {
		/* fall through */
	}
	return false;
}

// ---------------------------------------------------------------------------
// Remote operations
// ---------------------------------------------------------------------------

const BASE = "/js-pixel";

export async function createSnippet(
	draft: ValidatedSnippetDraft,
): Promise<JsPixelSnippetView> {
	const res = await apiClient.post<CreateSnippetResponse>(`${BASE}/snippet`, {
		siteLabel: draft.siteLabel,
		allowedOrigin: draft.allowedOrigin,
		eventTypes: draft.eventTypes,
	});
	return res.snippet;
}

export async function listSnippets(): Promise<JsPixelSnippetView[]> {
	const res = await apiClient.get<ListSnippetsResponse>(`${BASE}/snippet`);
	return res.snippets ?? [];
}

export async function updateSnippet(
	id: string,
	patch: UpdatePatch,
): Promise<JsPixelSnippetView> {
	const res = await apiClient.put<UpdateSnippetResponse>(
		`${BASE}/snippet/${encodeURIComponent(id)}`,
		{
			...(patch.siteLabel !== undefined
				? { siteLabel: patch.siteLabel }
				: {}),
			...(patch.allowedOrigin !== undefined
				? { allowedOrigin: patch.allowedOrigin }
				: {}),
			...(patch.eventTypes !== undefined
				? { eventTypes: patch.eventTypes }
				: {}),
			...(patch.enabled !== undefined ? { enabled: patch.enabled } : {}),
		},
	);
	return res.snippet;
}

export async function deleteSnippet(id: string): Promise<void> {
	try {
		await apiClient.delete<DeleteSnippetResponse>(
			`${BASE}/snippet/${encodeURIComponent(id)}`,
		);
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return;
		throw err;
	}
}

export async function listEvents(
	id: string,
	limit = 50,
): Promise<JsPixelEventView[]> {
	try {
		const res = await apiClient.get<ListEventsResponse>(
			`${BASE}/events/${encodeURIComponent(id)}`,
			{ query: { limit } },
		);
		return res.events ?? [];
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return [];
		throw err;
	}
}

// ---------------------------------------------------------------------------
// TanStack Query keys
// ---------------------------------------------------------------------------

export const jsPixelQueryKeys = {
	all: ["js-pixel"] as const,
	list: () => [...jsPixelQueryKeys.all, "list"] as const,
	events: (id: string) =>
		[...jsPixelQueryKeys.all, "events", id || "__none__"] as const,
};

// ---------------------------------------------------------------------------
// Cache mutation helpers
// ---------------------------------------------------------------------------

/** Upsert the given snippet into a cached list — used by create/update success
 *  handlers so the UI reflects the change without a full refetch. */
export function upsertSnippet(
	rows: readonly JsPixelSnippetView[],
	row: JsPixelSnippetView,
): JsPixelSnippetView[] {
	let hit = false;
	const next = rows.map((r) => {
		if (r.id === row.id) {
			hit = true;
			return row;
		}
		return r;
	});
	if (!hit) next.unshift(row);
	return next;
}

export function removeSnippet(
	rows: readonly JsPixelSnippetView[],
	id: string,
): JsPixelSnippetView[] {
	return rows.filter((r) => r.id !== id);
}
